import { Type, type Static } from '@sinclair/typebox';
import { StringEnum, createSchema } from '../../util/schema';

/** RFC 7807 Problem Details */
export const ProblemDetails = Type.Object(
  {
    type: Type.Optional(Type.String({ default: 'about:blank' })),
    title: Type.String(),
    status: Type.Integer(),
    detail: Type.Optional(Type.String()),
    instance: Type.Optional(Type.String()), // URI reference for this occurrence (e.g. path to an existing conflicting resource).
  },
  { $id: 'ProblemDetails', description: 'RFC 7807 Problem Details' },
);
export type ProblemDetails = Static<typeof ProblemDetails>;
export const problemDetailsSchema = createSchema(ProblemDetails);

/** Opaque PDF body (`application/pdf`). */
export const PdfDocument = Type.String({
  $id: 'PdfDocument',
  description: 'PDF document bytes',
  contentMediaType: 'application/pdf',
  contentEncoding: 'binary',
});
export type PdfDocument = Static<typeof PdfDocument>;
export const pdfDocumentSchema = createSchema(PdfDocument);

/** Opaque HTML body (`text/html`). */
export const HtmlDocument = Type.String({
  $id: 'HtmlDocument',
  description: 'HTML document bytes',
  contentMediaType: 'text/html',
  contentEncoding: 'binary',
});
export type HtmlDocument = Static<typeof HtmlDocument>;
export const htmlDocumentSchema = createSchema(HtmlDocument);

/** ISO calendar date `YYYY-MM-DD`. */
export const IsoDate = Type.String({
  $id: 'IsoDate',
  pattern: '^\\d{4}-\\d{2}-\\d{2}$',
  description: 'ISO calendar date YYYY-MM-DD',
  format: 'date',
});
export type IsoDate = Static<typeof IsoDate>;
export const isoDateSchema = createSchema(IsoDate);

/** Calendar month `YYYY-MM`. */
export const YearMonth = Type.String({
  $id: 'YearMonth',
  pattern: '^\\d{4}-\\d{2}$',
  description: 'Calendar month YYYY-MM',
});
export type YearMonth = Static<typeof YearMonth>;
export const yearMonthSchema = createSchema(YearMonth);

/** UTC ISO 8601 timestamp. */
export const IsoDateTime = Type.String({
  $id: 'IsoDateTime',
  minLength: 1,
  description: 'UTC ISO 8601 timestamp',
  format: 'date-time',
});
export type IsoDateTime = Static<typeof IsoDateTime>;
export const isoDateTimeSchema = createSchema(IsoDateTime);

export const TextAlign = StringEnum(['left', 'center', 'right', 'justify'], {
  $id: 'TextAlign',
});
export type TextAlign = Static<typeof TextAlign>;

/** Layout width hint for panels / boxes (4-col grid: compact=1/4, normal=1/2, wide=3/4, full). */
export const SectionSpan = StringEnum(['compact', 'normal', 'wide', 'full'], {
  $id: 'SectionSpan',
});
export type SectionSpan = Static<typeof SectionSpan>;
